import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inspectorsApi } from '../../api/client';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Input, { Select, Label } from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { Plus, Edit, Trash2, Search, Check, X, UserCheck, UserX, Building2, Phone } from 'lucide-react';

export default function InspectorsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [inspectorModal, setInspectorModal] = useState({ open: false, inspector: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, inspector: null });

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    isActive: true,
  });

  const [formError, setFormError] = useState('');

  // Fetch inspectors
  const { data: inspectorsData, isLoading } = useQuery({
    queryKey: ['inspectors', search, companyFilter],
    queryFn: () => inspectorsApi.getAll({ search, company: companyFilter }),
    select: (res) => res.data,
  });

  // Fetch companies for filter
  const { data: companies } = useQuery({
    queryKey: ['inspector-companies'],
    queryFn: () => inspectorsApi.getCompanies(),
    select: (res) => res.data,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => inspectorsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['inspectors']);
      setInspectorModal({ open: false, inspector: null });
      resetForm();
    },
    onError: (error) => {
      if (error.response?.status === 409) {
        setFormError(t('inspectors.duplicateError', '此巡檢人員已存在（姓名+電話重複）'));
      } else {
        setFormError(error.response?.data?.error || t('common.error'));
      }
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => inspectorsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['inspectors']);
      queryClient.invalidateQueries(['inspector-companies']);
      setInspectorModal({ open: false, inspector: null });
      resetForm();
    },
    onError: (error) => {
      setFormError(error.response?.data?.error || t('common.error'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => inspectorsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['inspectors']);
      setDeleteModal({ open: false, inspector: null });
    },
  });

  // 停用 / 啟用
  const activeMutation = useMutation({
    mutationFn: ({ id, isActive }) => inspectorsApi.setActive(id, isActive),
    onSuccess: () => queryClient.invalidateQueries(['inspectors']),
  });

  // Work items affected by deleting the selected inspector
  const { data: deleteUsage } = useQuery({
    queryKey: ['inspector-usage', deleteModal.inspector?.id],
    queryFn: () => inspectorsApi.getUsage(deleteModal.inspector.id),
    select: (res) => res.data,
    enabled: deleteModal.open && Boolean(deleteModal.inspector?.id),
  });

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      phone: '',
      isActive: true,
    });
    setFormError('');
  };

  const openCreateModal = () => {
    resetForm();
    setInspectorModal({ open: true, inspector: null });
  };

  const openEditModal = (inspector) => {
    setFormData({
      name: inspector.name,
      company: inspector.company || '',
      phone: inspector.phone || '',
      isActive: Boolean(inspector.isActive),
    });
    setFormError('');
    setInspectorModal({ open: true, inspector });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError(t('inspectors.nameRequired', '請輸入姓名'));
      return;
    }

    const submitData = {
      name: formData.name.trim(),
      company: formData.company.trim() || null,
      phone: formData.phone.trim() || null,
      isActive: formData.isActive,
    };

    if (inspectorModal.inspector) {
      updateMutation.mutate({ id: inspectorModal.inspector.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const inspectors = inspectorsData?.data || [];
  const pagination = inspectorsData?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('inspectors.title', '巡檢人員管理')}
        </h1>
        <Button onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" />
          {t('inspectors.new', '新增巡檢人員')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('inspectors.searchPlaceholder', '搜尋姓名或電話...')}
                className="pl-9"
              />
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                <option value="">{t('inspectors.allCompanies', '全部監造單位')}</option>
                {companies?.map((company) => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Inspectors Table */}
      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tpc-blue"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('inspectors.name', '姓名')}</TableHead>
                  <TableHead>{t('inspectors.company', '監造單位')}</TableHead>
                  <TableHead>{t('inspectors.phone', '電話')}</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inspectors.length > 0 ? (
                  inspectors.map((inspector) => (
                    <TableRow key={inspector.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{inspector.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {inspector.company ? (
                          <div className="flex items-center gap-1 text-gray-600">
                            <Building2 className="w-4 h-4" />
                            {inspector.company}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {inspector.phone ? (
                          <div className="flex items-center gap-1 text-gray-600">
                            <Phone className="w-4 h-4" />
                            {inspector.phone}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {inspector.isActive ? (
                          <Badge variant="success">
                            <Check className="w-3 h-3 mr-1" />
                            {t('users.active', '啟用')}
                          </Badge>
                        ) : (
                          <Badge variant="danger">
                            <X className="w-3 h-3 mr-1" />
                            {t('users.inactive', '停用')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(inspector)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title={inspector.isActive ? t('users.deactivate', '停用') : t('users.activate', '啟用')}
                            onClick={() => activeMutation.mutate({ id: inspector.id, isActive: !inspector.isActive })}
                          >
                            {inspector.isActive
                              ? <UserX className="w-4 h-4 text-amber-600" />
                              : <UserCheck className="w-4 h-4 text-green-600" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title={t('common.delete')}
                            onClick={() => setDeleteModal({ open: true, inspector })}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-12">
                      {t('common.noData', '無資料')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardBody>
        {pagination && pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t text-sm text-gray-500 text-center">
            {t('common.pagination', '第 {{page}} 頁，共 {{total}} 頁', {
              page: pagination.page,
              total: pagination.totalPages,
            })}
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={inspectorModal.open}
        onClose={() => setInspectorModal({ open: false, inspector: null })}
        title={inspectorModal.inspector ? t('inspectors.edit', '編輯巡檢人員') : t('inspectors.new', '新增巡檢人員')}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {formError}
            </div>
          )}

          <div>
            <Label required>{t('inspectors.name', '姓名')}</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={t('inspectors.namePlaceholder', '例如：陳XX')}
            />
          </div>

          <div>
            <Label>{t('inspectors.company', '監造單位')}</Label>
            <Input
              value={formData.company}
              onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
              placeholder={t('inspectors.companyPlaceholder', '例如：中興監造單位')}
              list="company-suggestions"
            />
            <datalist id="company-suggestions">
              {companies?.map((company) => (
                <option key={company} value={company} />
              ))}
            </datalist>
          </div>

          <div>
            <Label>{t('inspectors.phone', '電話')}</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
              placeholder={t('inspectors.phonePlaceholder', '例如：0912-345678')}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('inspectors.phoneHint', '姓名 + 電話 為唯一識別，不可重複')}
            </p>
          </div>

          {inspectorModal.inspector && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">
                {t('users.active', '啟用')}
              </label>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setInspectorModal({ open: false, inspector: null })}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, inspector: null })}
        title={t('common.confirm')}
        size="sm"
      >
        <div className="space-y-4">
          <p>{t('inspectors.deleteConfirm', '確定要永久刪除此巡檢人員嗎？')}</p>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="font-medium">{deleteModal.inspector?.name}</p>
            {deleteModal.inspector?.company && (
              <p className="text-sm text-gray-500">{deleteModal.inspector.company}</p>
            )}
            {deleteModal.inspector?.phone && (
              <p className="text-sm text-gray-500">{deleteModal.inspector.phone}</p>
            )}
          </div>
          {deleteUsage?.workItems > 0 ? (
            <p className="text-sm text-red-600">
              {t('inspectors.deleteImpact', '刪除後無法復原，此巡檢人員將從 {{count}} 筆工作項目中移除。若只是暫不使用，建議改用「停用」。', { count: deleteUsage.workItems })}
            </p>
          ) : (
            <p className="text-sm text-gray-500">{t('inspectors.deleteUnused', '此巡檢人員尚未被任何工作項目使用，可安全刪除。')}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setDeleteModal({ open: false, inspector: null })}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(deleteModal.inspector?.id)}
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
